"use client";

import { useState } from "react";
import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import type { ITextMediaBlockImage, ITextMediaBlockVideo } from "./TextMediaBlock.props";

const getAutoplayEmbedUrl = (url: string) => `${url}${url.includes("?") ? "&" : "?"}autoplay=1`;

const getPosterAlt = (poster: ITextMediaBlockImage) => poster.alternateText;

export const TextMediaVideo = ({
  poster,
  video,
}: {
  poster?: ITextMediaBlockImage;
  video: ITextMediaBlockVideo;
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const Image = resolveKaajPrimitive("Image");
  const embedUrl = video.embedUrl;

  if (!embedUrl) {
    return null;
  }

  const src = isPlaying ? getAutoplayEmbedUrl(embedUrl) : embedUrl;

  if (!poster || isPlaying) {
    return (
      <iframe
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
        loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin"
        src={src}
        title={video.title}
      />
    );
  }

  return (
    <button
      aria-label={`Play ${video.title}`}
      className="group absolute inset-0 block h-full w-full cursor-pointer overflow-hidden text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-6px] focus-visible:outline-white"
      onClick={() => setIsPlaying(true)}
      type="button"
    >
      <Image
        alt={getPosterAlt(poster)}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out motion-reduce:transition-none motion-safe:group-hover:scale-[1.02]"
        decoding="async"
        height={poster.height ?? undefined}
        loading="lazy"
        sizes="(min-width: 1280px) 540px, (min-width: 1024px) 48vw, 92vw"
        src={poster.defaultSrc}
        width={poster.width ?? undefined}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(16,19,34,0.08)_0%,rgba(16,19,34,0.18)_45%,rgba(16,19,34,0.58)_100%)]"
      />
      <span className="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full bg-[#2144FF] px-3 py-3 text-sm font-extrabold text-white shadow-[0_24px_58px_rgba(33,68,255,0.38)] ring-1 ring-white/45 transition-[background-color,box-shadow,transform] duration-200 group-hover:bg-[#132EDB] group-hover:shadow-[0_28px_68px_rgba(33,68,255,0.46)] motion-reduce:transition-none motion-safe:group-hover:scale-[1.02] sm:px-5">
        <span className="grid size-11 place-items-center rounded-full bg-white text-[#2144FF] shadow-[inset_0_0_0_1px_rgba(33,68,255,0.1)] sm:size-12">
          <span className="ml-1 h-0 w-0 border-y-[8px] border-l-[13px] border-y-transparent border-l-current" />
        </span>
        <span className="hidden sm:inline">Play video</span>
      </span>
      {video.title ? (
        <span
          className={cn(
            "absolute left-3 right-3 top-3 max-w-[calc(100%-1.5rem)] rounded-[1.1rem] border border-white/70 bg-white/94 px-3 py-2 text-[#151927] shadow-[0_16px_42px_rgba(8,11,26,0.16)]",
            "sm:left-5 sm:right-auto sm:top-5 sm:max-w-[min(28rem,calc(100%-2.5rem))] sm:rounded-2xl sm:px-5 sm:py-3",
          )}
        >
          <span className="block truncate text-xs font-extrabold tracking-[-0.01em] text-[#151927] sm:text-sm">
            {video.title}
          </span>
        </span>
      ) : null}
    </button>
  );
};
